import {
  AnimatePresence,
  motion,
  useDragControls,
  useIsPresent,
  useReducedMotion,
} from 'framer-motion';
import { useCallback, useId, useRef, useState, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';

import { useTelegram } from '@/shared/hooks/useTelegram';
import { cn } from '@/shared/lib/cn';

import { CloseIcon } from '../lib/icons';
import { Portal } from '../lib/Portal';
import { useBodyScrollLock } from '../lib/useBodyScrollLock';
import { useFocusTrap } from '../lib/useFocusTrap';
import { useOnEscape } from '../lib/useOnEscape';

import styles from './Sheet.module.css';

export interface SheetProps {
  open: boolean;
  onClose: () => void;
  title?: string;
  className?: string;
  children?: ReactNode;
}

/** Порог закрытия: 96px пути или 500px/с скорости — как в нативных шитах. */
const DISMISS_OFFSET = 96;
const DISMISS_VELOCITY = 500;

/**
 * Bottom sheet с drag-to-dismiss.
 *
 * dragListener={false} + dragControls: жест начинается только с «ручки» и
 * заголовка. Если разрешить тянуть за всё тело, жест перехватывает скролл
 * контента и длинный список внутри sheet становится неуправляемым в WebView.
 */
function SheetSurface({
  onClose,
  title,
  className,
  children,
}: Omit<SheetProps, 'open'>): React.ReactElement {
  const { t } = useTranslation();
  const { haptic } = useTelegram();
  const panelRef = useRef<HTMLDivElement>(null);
  const entered = useRef(false);
  const titleId = useId();
  const reduceMotion = useReducedMotion();
  const present = useIsPresent();
  const dragControls = useDragControls();
  const [dragging, setDragging] = useState(false);

  const close = useCallback(() => {
    if (!present) return;
    haptic('light');
    onClose();
  }, [haptic, onClose, present]);

  useBodyScrollLock(true);
  useOnEscape(true, close);
  useFocusTrap(panelRef, true);

  return (
    <div className={styles.root}>
      <motion.div
        className={styles.backdrop}
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        transition={{ duration: reduceMotion ? 0 : 0.2 }}
        onClick={present ? close : undefined}
      />

      <motion.div
        ref={panelRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby={title !== undefined ? titleId : undefined}
        tabIndex={-1}
        className={cn(styles.panel, dragging && styles.dragging, className)}
        initial={{ y: '100%' }}
        animate={{ y: 0 }}
        exit={{ y: '100%' }}
        transition={
          reduceMotion ? { duration: 0 } : { type: 'spring', damping: 32, stiffness: 340 }
        }
        onAnimationComplete={() => {
          entered.current = present;
        }}
        drag={reduceMotion ? false : 'y'}
        dragControls={dragControls}
        dragListener={false}
        dragConstraints={{ top: 0, bottom: 0 }}
        dragElastic={{ top: 0, bottom: 0.4 }}
        onDragStart={() => setDragging(true)}
        onDragEnd={(_event, info) => {
          setDragging(false);
          if (info.offset.y > DISMISS_OFFSET || info.velocity.y > DISMISS_VELOCITY) close();
        }}
      >
        <div
          className={styles.grabArea}
          onPointerDown={(event) => {
            if (reduceMotion || !present || !entered.current) return;
            if ((event.target as HTMLElement).closest('button, a, input, select, textarea')) return;
            dragControls.start(event);
          }}
        >
          <span className={styles.handle} aria-hidden="true" />
          {title !== undefined ? (
            <div className={styles.header}>
              <h2 className={styles.title} id={titleId}>
                {title}
              </h2>
              <button
                type="button"
                className={styles.close}
                onClick={close}
                aria-label={t('common.close')}
              >
                <CloseIcon size={18} />
              </button>
            </div>
          ) : null}
        </div>

        <div className={styles.body}>{children}</div>
      </motion.div>
    </div>
  );
}

/** The surface owns its lock for the complete enter/exit lifecycle. */
export function Sheet({ open, ...props }: SheetProps): React.ReactElement {
  return (
    <Portal>
      <AnimatePresence>{open ? <SheetSurface {...props} /> : null}</AnimatePresence>
    </Portal>
  );
}
